import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  MapPin,
  Check,
  UserPlus,
  Users2,
  ArrowLeft,
  Send,
  AlertCircle,
  Share2,
  Pencil,
  Plus,
  Trash2,
  X,
  Globe,
} from 'lucide-react';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import {
  fetchArtistBySlugOrId,
  normalizeInstagramUsername,
  normalizeGenre,
  sanitizeExternalUrl,
  formatSpotifyUrl,
  formatAppleMusicUrl,
  formatYoutubeMusicUrl,
  formatYoutubeUrl,
  formatInstagramUrl,
} from '../../lib/artistWebService';
import { sendCollabRequest } from '../../lib/notificationService';
import Navbar from '../common/Navbar';
import Footer from '../common/Footer';
import SEO from '../common/SEO';
import '../../styles/ArtistPersonalPage.css';

/* ==========================================================================
   High-Fidelity Platform Icon Components
   ========================================================================== */

function InstagramIcon() {
  return (
    <div
      className="sx-platform-icon-wrap"
      style={{
        background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
      }}
    >
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#ffffff"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
        <circle cx="12" cy="12" r="4" />
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
      </svg>
    </div>
  );
}

function YouTubeIcon() {
  return (
    <div className="sx-platform-icon-wrap" style={{ background: '#FF0000' }}>
      <svg width="26" height="26" viewBox="0 0 24 24" fill="#ffffff">
        <path d="M21.58 7.19a2.5 2.5 0 0 0-1.76-1.77C18.26 5 12 5 12 5s-6.26 0-7.82.42A2.5 2.5 0 0 0 2.42 7.19C2 8.75 2 12 2 12s0 3.25.42 4.81a2.5 2.5 0 0 0 1.76 1.77C5.74 19 12 19 12 19s6.26 0 7.82-.42a2.5 2.5 0 0 0 1.76-1.77C22 15.25 22 12 22 12s0-3.25-.42-4.81zM10 15V9l5.2 3-5.2 3z" />
      </svg>
    </div>
  );
}

function SpotifyIcon() {
  return (
    <div className="sx-platform-icon-wrap is-circle" style={{ background: '#1DB954' }}>
      <svg width="26" height="26" viewBox="0 0 24 24" fill="#000000">
        <path d="M17.5 10.8c-2.3-1.4-6.1-1.5-8.3-.9-.4.1-.7-.1-.9-.4-.1-.4.1-.7.4-.9 2.5-.7 6.8-.6 9.4 1 0 .2.2.4.2.6-.4.4-.6.6-.8.6z" />
        <path d="M16.4 13.2c-.3.4-.8.5-1.1.2-1.9-1.2-4.8-1.5-7.1-.8-.4.1-.8-.1-1-.5-.1-.4.1-.8.5-1 2.6-.8 5.9-.4 8.1.9.4.3.4.8.1 1.2z" />
        <path d="M15.3 15.6c-.2.3-.6.4-.9.2-1.6-1-3.7-1.2-6.1-.7-.4.1-.7-.2-.8-.5-.1-.4.2-.7.5-.8 2.7-.6 5.1-.3 6.9.8.4.2.5.7.4 1z" />
      </svg>
    </div>
  );
}

function AppleMusicIcon() {
  return (
    <div
      className="sx-platform-icon-wrap"
      style={{ background: 'linear-gradient(135deg, #fc3c44, #fa243c, #f94c57)' }}
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="#ffffff">
        <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
      </svg>
    </div>
  );
}

function JioSaavnIcon() {
  return (
    <div className="sx-platform-icon-wrap is-circle" style={{ background: '#12C2A2' }}>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="#ffffff">
        <path d="M12 3c-.5 2.5-2 5-4.5 6.5 1.5.5 3.5.5 4.5-.5 1 1 3 1 4.5.5C14 8 12.5 5.5 12 3zm-6 8c-1.5 2-2 4.5-1.5 7 2-.5 4-2 5-4-1.5-.5-2.5-1.5-3.5-3zm12 0c-1 1.5-2 2.5-3.5 3 1 2 3 3.5 5 4 .5-2.5 0-5-1.5-7zm-8 4.5c-.5 2 0 4 1.5 5.5 1.5-1.5 2-3.5 1.5-5.5-.5.5-1.5.5-3 0zm4 0c-.5.5-1.5.5-2 0-.5 2 0 4 1.5 5.5 1.5-1.5 2-3.5 1.5-5.5-.5.5-1 .5-1 0z" />
      </svg>
    </div>
  );
}

function YouTubeMusicIcon() {
  return (
    <div className="sx-platform-icon-wrap is-circle" style={{ background: '#FF0000' }}>
      <svg width="26" height="26" viewBox="0 0 24 24" fill="#ffffff">
        <circle cx="12" cy="12" r="9" fill="none" stroke="#ffffff" strokeWidth="2.2" />
        <polygon points="10,8 16,12 10,16" fill="#ffffff" />
      </svg>
    </div>
  );
}

function AmazonMusicIcon() {
  return (
    <div className="sx-platform-icon-wrap" style={{ background: '#25D1DA' }}>
      <svg width="28" height="28" viewBox="0 0 24 24">
        <text
          x="12"
          y="10"
          fill="#0c1b33"
          fontSize="7"
          fontWeight="bold"
          textAnchor="middle"
          fontFamily="sans-serif"
        >
          music
        </text>
        <path d="M5 15c4 3 10 3 14 0" stroke="#0c1b33" strokeWidth="2.2" strokeLinecap="round" fill="none" />
        <path
          d="M18 13l2 2-2 2"
          stroke="#0c1b33"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
    </div>
  );
}

function SoundCloudIcon() {
  return (
    <div
      className="sx-platform-icon-wrap is-circle"
      style={{ background: 'linear-gradient(135deg, #ff8800, #ff3300)' }}
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="#ffffff">
        <path d="M19.5 10c-.3 0-.6.04-.9.1A5.002 5.002 0 0 0 9 12c0 .2.02.4.05.6A3.5 3.5 0 0 0 6.5 15c0 1.93 1.57 3.5 3.5 3.5h9.5c2.48 0 4.5-2.02 4.5-4.5S21.98 10 19.5 10z" />
      </svg>
    </div>
  );
}

function BandcampIcon() {
  return (
    <div className="sx-platform-icon-wrap" style={{ background: '#1da0c3' }}>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="#ffffff">
        <polygon points="4,18 9,6 20,6 15,18" />
      </svg>
    </div>
  );
}

function BeatStarsIcon() {
  return (
    <div className="sx-platform-icon-wrap is-circle" style={{ background: '#e62b4c' }}>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="#ffffff">
        <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
      </svg>
    </div>
  );
}

function CustomLinkIcon() {
  return (
    <div
      className="sx-platform-icon-wrap is-circle"
      style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}
    >
      <Globe size={22} color="#ffffff" />
    </div>
  );
}

/* ==========================================================================
   Predefined Platforms Registry
   ========================================================================== */

const PREDEFINED_PLATFORMS = [
  { key: 'spotify', name: 'Spotify', placeholder: 'https://open.spotify.com/artist/...' },
  { key: 'instagram', name: 'Instagram', placeholder: 'https://instagram.com/username or @username' },
  { key: 'youtube', name: 'YouTube', placeholder: 'https://youtube.com/@channel' },
  { key: 'apple_music', name: 'Apple Music', placeholder: 'https://music.apple.com/artist/...' },
  { key: 'jiosaavn', name: 'JioSaavn', placeholder: 'https://www.jiosaavn.com/artist/...' },
  { key: 'youtube_music', name: 'YouTube Music', placeholder: 'https://music.youtube.com/channel/...' },
  { key: 'amazon_music', name: 'Amazon Music', placeholder: 'https://music.amazon.com/artists/...' },
  { key: 'soundcloud', name: 'SoundCloud', placeholder: 'https://soundcloud.com/artist' },
  { key: 'bandcamp', name: 'Bandcamp', placeholder: 'https://artist.bandcamp.com' },
  { key: 'beatstars', name: 'BeatStars', placeholder: 'https://beatstars.com/producer' },
  { key: 'custom', name: 'Custom Platform', placeholder: 'https://yourwebsite.com' },
];

function getPlatformIcon(platformKey) {
  switch (platformKey) {
    case 'instagram':
      return <InstagramIcon />;
    case 'youtube':
      return <YouTubeIcon />;
    case 'spotify':
      return <SpotifyIcon />;
    case 'apple_music':
      return <AppleMusicIcon />;
    case 'jiosaavn':
      return <JioSaavnIcon />;
    case 'youtube_music':
      return <YouTubeMusicIcon />;
    case 'amazon_music':
      return <AmazonMusicIcon />;
    case 'soundcloud':
      return <SoundCloudIcon />;
    case 'bandcamp':
      return <BandcampIcon />;
    case 'beatstars':
      return <BeatStarsIcon />;
    default:
      return <CustomLinkIcon />;
  }
}

/* ==========================================================================
   Main Artist Individual Personal Page Component
   ========================================================================== */

export default function ArtistProfilePage() {
  const { slug, instagramUsername } = useParams();
  const navigate = useNavigate();
  const { user, profile, refreshUserData } = useAuth();

  // Target lookup key: URL param slug, instagram handle, profile handle, or auth user ID
  const targetSlug =
    instagramUsername ||
    slug ||
    profile?.instagram ||
    profile?.stage_name ||
    user?.id;

  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [avatarError, setAvatarError] = useState(false);

  // Follow states
  const [isFollowing, setIsFollowing] = useState(false);
  const [followPending, setFollowPending] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);

  // Collab modal states
  const [showCollabModal, setShowCollabModal] = useState(false);
  const [collabMessage, setCollabMessage] = useState('');
  const [collabProjectType, setCollabProjectType] = useState('Feature / Vocal');
  const [collabSubmitting, setCollabSubmitting] = useState(false);
  const [collabSubmitted, setCollabSubmitted] = useState(false);
  const [collabError, setCollabError] = useState('');

  // ── Edit Mode & Link Management States (Artist-Only) ──
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingLinks, setEditingLinks] = useState([]);
  const [editErrors, setEditErrors] = useState({});
  const [editGlobalError, setEditGlobalError] = useState('');
  const [editSuccessMsg, setEditSuccessMsg] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // Dedicated DB links loaded from artist_platform_links table
  const [dbPlatformLinks, setDbPlatformLinks] = useState(null);

  // 1. Fetch artist details from Supabase
  useEffect(() => {
    let isMounted = true;
    async function loadArtist() {
      if (!targetSlug) {
        if (isMounted) setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await fetchArtistBySlugOrId(targetSlug);
        if (isMounted) {
          setArtist(data);
        }
      } catch (err) {
        console.error('Failed to load artist:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    loadArtist();
    return () => {
      isMounted = false;
    };
  }, [targetSlug]);

  // 2. Fetch artist_platform_links table rows if available
  useEffect(() => {
    if (!artist?.user_id && !artist?.id) return;
    let isMounted = true;

    async function loadPlatformLinksTable() {
      try {
        const artistId = artist.user_id || artist.id;
        const { data, error } = await supabase
          .from('artist_platform_links')
          .select('*')
          .eq('artist_id', artistId)
          .eq('is_active', true)
          .order('display_order', { ascending: true });

        if (!error && Array.isArray(data) && data.length > 0) {
          if (isMounted) setDbPlatformLinks(data);
        }
      } catch (err) {
        // Migration table may not exist yet; safe to ignore
      }
    }

    loadPlatformLinksTable();
    return () => {
      isMounted = false;
    };
  }, [artist]);

  // 3. Fetch real follower count & follow status
  useEffect(() => {
    if (!artist?.user_id || !isSupabaseConfigured()) return;
    let isMounted = true;

    async function loadFollows() {
      try {
        const { count } = await supabase
          .from('artist_follows')
          .select('*', { count: 'exact', head: true })
          .eq('artist_user_id', artist.user_id);

        if (isMounted && typeof count === 'number') {
          setFollowerCount(count);
        }

        if (user?.id) {
          const { data: followRow } = await supabase
            .from('artist_follows')
            .select('id')
            .eq('follower_user_id', user.id)
            .eq('artist_user_id', artist.user_id)
            .maybeSingle();

          if (isMounted) {
            setIsFollowing(!!followRow);
          }
        }
      } catch (err) {
        console.error('Failed to check follow status:', err);
      }
    }

    loadFollows();
    return () => {
      isMounted = false;
    };
  }, [artist, user]);

  // Follow toggle handler
  const handleToggleFollow = useCallback(async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (!artist?.user_id || user.id === artist.user_id || followPending) return;
    setFollowPending(true);
    const targetArtistId = artist.user_id;

    try {
      if (isFollowing) {
        await supabase
          .from('artist_follows')
          .delete()
          .eq('follower_user_id', user.id)
          .eq('artist_user_id', targetArtistId);
        setIsFollowing(false);
        setFollowerCount((prev) => Math.max(0, prev - 1));
      } else {
        await supabase
          .from('artist_follows')
          .insert({ follower_user_id: user.id, artist_user_id: targetArtistId });
        setIsFollowing(true);
        setFollowerCount((prev) => prev + 1);
      }
    } catch (err) {
      console.error('Error toggling follow:', err);
    } finally {
      setFollowPending(false);
    }
  }, [user, artist, isFollowing, followPending, navigate]);

  // Collab submit handler
  const handleCollabSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate('/login');
      return;
    }
    if (!artist?.user_id) return;

    setCollabSubmitting(true);
    setCollabError('');

    try {
      const res = await sendCollabRequest({
        senderUserId: user.id,
        recipientUserId: artist.user_id,
        projectType: collabProjectType,
        message: collabMessage,
      });

      if (!res.success) {
        setCollabError(res.error || 'Failed to send collaboration request.');
      } else {
        setCollabSubmitted(true);
        setTimeout(() => {
          setShowCollabModal(false);
          setCollabSubmitted(false);
          setCollabMessage('');
        }, 2000);
      }
    } catch (err) {
      setCollabError(err.message || 'Error occurred while sending proposal.');
    } finally {
      setCollabSubmitting(false);
    }
  };

  // Ownership verification: strictly authenticated user ID === artist's user_id or id
  const isOwner = Boolean(
    user &&
    artist &&
    (user.id === artist.user_id || user.id === artist.id)
  );

  // Resolved public Instagram identifier & canonical URL
  const publicInstagram =
    artist?.instagram_username ||
    (targetSlug ? normalizeInstagramUsername(targetSlug) : '') ||
    artist?.slug ||
    '';
  const canonicalUrl = `https://senritsux.in/artist/${publicInstagram || artist?.slug || ''}`;

  const handleCopyProfileLink = () => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(canonicalUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  // ── Construct active public platform links ──
  const availablePlatformLinks = [];

  if (dbPlatformLinks && dbPlatformLinks.length > 0) {
    // If dedicated artist_platform_links rows exist, use them
    dbPlatformLinks.forEach((row) => {
      const sanitized = sanitizeExternalUrl(row.url);
      if (sanitized) {
        availablePlatformLinks.push({
          id: row.id,
          name: row.platform_name || 'Link',
          url: sanitized,
          icon: getPlatformIcon(row.platform_key),
          label: row.platform_name,
        });
      }
    });
  } else if (artist) {
    // Parse any custom links saved in bio JSON fallback
    let customLinksFromBio = [];
    try {
      if (artist.bio && typeof artist.bio === 'string' && artist.bio.startsWith('{')) {
        const parsed = JSON.parse(artist.bio);
        if (Array.isArray(parsed?.custom_links)) {
          customLinksFromBio = parsed.custom_links;
        }
      }
    } catch {
      // Non-JSON bio; ignore
    }

    const spotifyUrl = sanitizeExternalUrl(artist.spotify_link);
    const appleMusicUrl = sanitizeExternalUrl(artist.apple_music_link);
    const ytMusicUrl = sanitizeExternalUrl(artist.youtube_music_link);
    const youtubeUrl = sanitizeExternalUrl(artist.youtube);
    const instagramUrl = publicInstagram
      ? `https://www.instagram.com/${publicInstagram}/`
      : sanitizeExternalUrl(artist.instagram);
    const jiosaavnUrl = sanitizeExternalUrl(artist.jiosaavn_link || artist.jiosaavn);
    const amazonMusicUrl = sanitizeExternalUrl(artist.amazon_music_link || artist.amazon_music);

    if (instagramUrl) {
      availablePlatformLinks.push({
        id: 'instagram',
        name: 'Instagram',
        url: instagramUrl,
        icon: <InstagramIcon />,
        label: 'Instagram',
      });
    }

    if (youtubeUrl) {
      availablePlatformLinks.push({
        id: 'youtube',
        name: 'YouTube',
        url: youtubeUrl,
        icon: <YouTubeIcon />,
        label: 'YouTube',
      });
    }

    if (spotifyUrl) {
      availablePlatformLinks.push({
        id: 'spotify',
        name: 'Spotify',
        url: spotifyUrl,
        icon: <SpotifyIcon />,
        label: 'Spotify',
      });
    }

    if (appleMusicUrl) {
      availablePlatformLinks.push({
        id: 'apple_music',
        name: 'Apple Music',
        url: appleMusicUrl,
        icon: <AppleMusicIcon />,
        labelComponent: (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}>
            <svg width="18" height="18" viewBox="0 0 170 170" fill="currentColor">
              <path d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.19-2.12-9.97-3.17-14.34-3.17-4.58 0-9.49 1.05-14.75 3.17-5.26 2.13-9.5 3.24-12.74 3.35-4.35.13-9.16-1.9-14.42-6.08-3.7-3.04-7.66-7.74-11.89-14.09-6.3-9.44-11.19-20.17-14.68-32.2-3.48-12.04-5.23-23.47-5.23-34.3 0-14.12 3.36-26.13 10.08-36.03 6.72-9.9 15.35-14.94 25.88-15.12 4.47 0 9.53 1.25 15.18 3.75 5.66 2.5 9.4 3.84 11.23 4.02 1.41-.18 5.37-1.61 11.88-4.29 6.52-2.68 12.04-3.84 16.58-3.48 12.82.9 22.84 5.79 30.08 14.69-11.45 6.9-17.06 16.5-16.84 28.8.22 9.8 4.05 17.84 11.49 24.12 7.44 6.27 16.14 9.87 26.09 10.8-2.61 7.6-5.8 15.02-9.56 22.25zM119.22 33.15c0-7.23 2.65-14.07 7.95-20.52 5.3-6.45 11.75-10.74 19.35-12.87.54 3.2.68 5.86.42 7.97-.68 5.86-3.23 11.8-7.65 17.81-4.42 6.01-10.15 9.87-17.18 11.58-.9-1.34-1.99-2.99-2.89-3.97z" />
            </svg>
            Music
          </span>
        ),
      });
    }

    if (jiosaavnUrl) {
      availablePlatformLinks.push({
        id: 'jiosaavn',
        name: 'JioSaavn',
        url: jiosaavnUrl,
        icon: <JioSaavnIcon />,
        label: 'JioSaavn',
      });
    }

    if (ytMusicUrl) {
      availablePlatformLinks.push({
        id: 'youtube_music',
        name: 'YouTube Music',
        url: ytMusicUrl,
        icon: <YouTubeMusicIcon />,
        label: 'YouTube Music',
      });
    }

    if (amazonMusicUrl) {
      availablePlatformLinks.push({
        id: 'amazon_music',
        name: 'Amazon Music',
        url: amazonMusicUrl,
        icon: <AmazonMusicIcon />,
        labelComponent: (
          <span>
            <span style={{ color: '#ffffff' }}>amazon</span>{' '}
            <span style={{ color: '#25D1DA' }}>music</span>
          </span>
        ),
      });
    }

    // Append custom links from bio JSON
    customLinksFromBio.forEach((c) => {
      const sanitized = sanitizeExternalUrl(c.url);
      if (sanitized) {
        availablePlatformLinks.push({
          id: c.id || `custom-${Math.random()}`,
          name: c.name || 'Link',
          url: sanitized,
          icon: getPlatformIcon(c.platform),
          label: c.name || 'Website',
        });
      }
    });
  }

  // ── Edit Mode Handlers (Artist Only) ──
  const handleOpenEdit = () => {
    if (!isOwner) return;

    // Populate editingLinks from current state
    const currentList = [];

    if (dbPlatformLinks && dbPlatformLinks.length > 0) {
      dbPlatformLinks.forEach((l) => {
        currentList.push({
          id: l.id || String(Math.random()),
          platform: l.platform_key || 'custom',
          name: l.platform_name || 'Link',
          url: l.url || '',
        });
      });
    } else {
      // Core 5 platforms
      if (artist?.spotify_link) {
        currentList.push({
          id: 'spotify',
          platform: 'spotify',
          name: 'Spotify',
          url: artist.spotify_link,
        });
      }
      if (artist?.instagram || artist?.raw_instagram) {
        const igVal = formatInstagramUrl(artist.raw_instagram || artist.instagram);
        currentList.push({
          id: 'instagram',
          platform: 'instagram',
          name: 'Instagram',
          url: igVal || '',
        });
      }
      if (artist?.youtube) {
        currentList.push({
          id: 'youtube',
          platform: 'youtube',
          name: 'YouTube',
          url: artist.youtube,
        });
      }
      if (artist?.apple_music_link) {
        currentList.push({
          id: 'apple_music',
          platform: 'apple_music',
          name: 'Apple Music',
          url: artist.apple_music_link,
        });
      }
      if (artist?.youtube_music_link) {
        currentList.push({
          id: 'youtube_music',
          platform: 'youtube_music',
          name: 'YouTube Music',
          url: artist.youtube_music_link,
        });
      }

      // Check bio JSON custom links
      try {
        if (artist?.bio && typeof artist.bio === 'string' && artist.bio.startsWith('{')) {
          const parsed = JSON.parse(artist.bio);
          if (Array.isArray(parsed?.custom_links)) {
            parsed.custom_links.forEach((cl) => {
              currentList.push({
                id: cl.id || String(Math.random()),
                platform: cl.platform || 'custom',
                name: cl.name || 'Link',
                url: cl.url || '',
              });
            });
          }
        }
      } catch {}
    }

    if (currentList.length === 0) {
      currentList.push({
        id: String(Math.random()),
        platform: 'spotify',
        name: 'Spotify',
        url: '',
      });
    }

    setEditingLinks(currentList);
    setEditErrors({});
    setEditGlobalError('');
    setEditSuccessMsg('');
    setShowEditModal(true);
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
    setEditErrors({});
    setEditGlobalError('');
    setEditSuccessMsg('');
  };

  const handleAddLink = () => {
    setEditingLinks((prev) => [
      ...prev,
      {
        id: String(Date.now() + Math.random()),
        platform: 'custom',
        name: 'Personal Link',
        url: '',
      },
    ]);
  };

  const handleRemoveLink = (linkId) => {
    setEditingLinks((prev) => prev.filter((item) => item.id !== linkId));
    setEditErrors((prev) => {
      const copy = { ...prev };
      delete copy[`url_${linkId}`];
      delete copy[`name_${linkId}`];
      return copy;
    });
  };

  const handlePlatformChange = (linkId, newPlatform) => {
    const defaultInfo = PREDEFINED_PLATFORMS.find((p) => p.key === newPlatform);
    setEditingLinks((prev) =>
      prev.map((item) => {
        if (item.id === linkId) {
          return {
            ...item,
            platform: newPlatform,
            name: defaultInfo ? defaultInfo.name : 'Link',
          };
        }
        return item;
      })
    );
  };

  const handleNameChange = (linkId, newName) => {
    setEditingLinks((prev) =>
      prev.map((item) => (item.id === linkId ? { ...item, name: newName } : item))
    );
    setEditErrors((prev) => {
      const copy = { ...prev };
      delete copy[`name_${linkId}`];
      return copy;
    });
  };

  const handleUrlChange = (linkId, newUrl) => {
    setEditingLinks((prev) =>
      prev.map((item) => (item.id === linkId ? { ...item, url: newUrl } : item))
    );
    setEditErrors((prev) => {
      const copy = { ...prev };
      delete copy[`url_${linkId}`];
      return copy;
    });
  };

  // ── Save Changes Handler (Validates & Persists via Supabase RLS) ──
  const handleSaveLinks = async (e) => {
    e.preventDefault();
    if (!user || !isOwner) {
      setEditGlobalError('Unauthorized: You can only edit links for your own profile.');
      return;
    }

    const newErrors = {};
    const seenUrls = new Set();
    const cleanedLinks = [];

    for (const link of editingLinks) {
      const rawUrl = (link.url || '').trim();
      const rawName = (link.name || '').trim();

      // If user added an extra blank row without typing anything, skip it cleanly
      if (!rawUrl && !rawName) continue;

      if (!rawName) {
        newErrors[`name_${link.id}`] = 'Please enter a platform label.';
      }

      if (!rawUrl) {
        newErrors[`url_${link.id}`] = 'Please enter a destination URL.';
      } else {
        let normalizedUrl = rawUrl;
        if (!/^https?:\/\//i.test(normalizedUrl)) {
          normalizedUrl = `https://${normalizedUrl}`;
        }

        try {
          const parsed = new URL(normalizedUrl);
          if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
            newErrors[`url_${link.id}`] = 'URL must start with http:// or https://';
          } else if (!parsed.hostname || !parsed.hostname.includes('.')) {
            newErrors[`url_${link.id}`] = 'Please enter a valid web domain address.';
          }
        } catch {
          newErrors[`url_${link.id}`] = 'Invalid URL format.';
        }

        const lowerUrl = normalizedUrl.toLowerCase().replace(/\/+$/, '');
        if (seenUrls.has(lowerUrl)) {
          newErrors[`url_${link.id}`] = 'Duplicate URL detected. Each link must be unique.';
        } else {
          seenUrls.add(lowerUrl);
        }

        cleanedLinks.push({
          ...link,
          name: rawName,
          url: normalizedUrl,
        });
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setEditErrors(newErrors);
      setEditGlobalError('Please fix the errors highlighted below before saving.');
      return;
    }

    setEditSaving(true);
    setEditGlobalError('');
    setEditSuccessMsg('');

    try {
      // 1. Identify primary destinations for core profile columns
      let newSpotify = null;
      let newInstagram = null;
      let newYoutube = null;
      let newAppleMusic = null;
      let newYoutubeMusic = null;
      const customLinks = [];

      cleanedLinks.forEach((item) => {
        const pKey = (item.platform || '').toLowerCase();
        if (pKey === 'spotify' && !newSpotify) {
          newSpotify = item.url;
        } else if (pKey === 'instagram' && !newInstagram) {
          newInstagram = normalizeInstagramUsername(item.url) || item.url;
        } else if (pKey === 'youtube' && !newYoutube) {
          newYoutube = item.url;
        } else if (pKey === 'apple_music' && !newAppleMusic) {
          newAppleMusic = item.url;
        } else if (pKey === 'youtube_music' && !newYoutubeMusic) {
          newYoutubeMusic = item.url;
        } else {
          customLinks.push({
            id: item.id,
            platform: item.platform || 'custom',
            name: item.name,
            url: item.url,
          });
        }
      });

      // 2. Update profiles table (RLS enforces auth.uid() = id)
      const profileUpdates = {
        spotify_link: newSpotify,
        instagram: newInstagram,
        youtube: newYoutube,
        apple_music_link: newAppleMusic,
        youtube_music_link: newYoutubeMusic,
        updated_at: new Date().toISOString(),
      };

      const { error: profErr } = await supabase
        .from('profiles')
        .update(profileUpdates)
        .eq('id', user.id);

      if (profErr) {
        console.error('Error updating profiles:', profErr);
        throw new Error(profErr.message || 'Database rejected profile update.');
      }

      // 3. Update artist_profiles table with custom_links stored in bio JSON
      const bioPayload = JSON.stringify({
        custom_links: customLinks,
      });

      const artistUpdates = {
        spotify_link: newSpotify,
        instagram: newInstagram,
        youtube: newYoutube,
        apple_music_link: newAppleMusic,
        youtube_music_link: newYoutubeMusic,
        bio: bioPayload,
        updated_at: new Date().toISOString(),
      };

      const { error: artErr } = await supabase
        .from('artist_profiles')
        .update(artistUpdates)
        .eq('user_id', user.id);

      if (artErr) {
        console.warn('Notice updating artist_profiles:', artErr);
      }

      // 4. Update dedicated artist_platform_links table if present
      let updatedDbLinks = null;
      try {
        const { error: delErr } = await supabase
          .from('artist_platform_links')
          .delete()
          .eq('artist_id', user.id);

        if (!delErr && cleanedLinks.length > 0) {
          const insertPayload = cleanedLinks.map((l, idx) => ({
            artist_id: user.id,
            platform_key: l.platform || 'custom',
            platform_name: l.name || 'Link',
            url: l.url,
            display_order: idx,
            is_active: true,
          }));
          const { data: inserted, error: insErr } = await supabase
            .from('artist_platform_links')
            .insert(insertPayload)
            .select();

          if (!insErr && inserted) {
            updatedDbLinks = inserted;
          }
        }
      } catch (err) {
        // Table not migrated yet; fallback handles persistence via profiles + bio
      }

      if (updatedDbLinks) {
        setDbPlatformLinks(updatedDbLinks);
      } else {
        setDbPlatformLinks(null);
      }

      // 5. Update local component state immediately
      setArtist((prev) => ({
        ...prev,
        spotify_link: newSpotify,
        instagram: newInstagram,
        youtube: newYoutube,
        apple_music_link: newAppleMusic,
        youtube_music_link: newYoutubeMusic,
        raw_instagram: newInstagram,
        instagram_username: normalizeInstagramUsername(newInstagram),
        bio: bioPayload,
      }));

      if (refreshUserData) {
        await refreshUserData();
      }

      setEditSuccessMsg('Platform links saved and updated successfully!');
      setTimeout(() => {
        setShowEditModal(false);
        setEditSuccessMsg('');
      }, 1200);
    } catch (err) {
      console.error('Error saving links:', err);
      setEditGlobalError(err.message || 'Failed to save changes. Please try again.');
    } finally {
      setEditSaving(false);
    }
  };

  // 4. Loading State
  if (loading) {
    return (
      <div className="sx-artist-personal-root">
        <Navbar />
        <div
          className="sx-artist-personal-main"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                width: '48px',
                height: '48px',
                border: '3px solid rgba(219, 255, 0, 0.2)',
                borderTopColor: '#DBFF00',
                borderRadius: '50%',
                animation: 'sxSpin 0.8s linear infinite',
                margin: '0 auto 1.25rem',
              }}
            />
            <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.95rem' }}>
              Loading Artist Profile...
            </p>
          </div>
        </div>
        <Footer />
        <style>{`@keyframes sxSpin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // 5. Not Found or Hidden State
  if (!artist || artist.is_visible === false) {
    return (
      <div className="sx-artist-personal-root">
        <SEO
          title="Artist Not Found | SenritsuX"
          description="The requested artist profile does not exist or has been set to private on SenritsuX."
          noindex={true}
        />
        <Navbar />
        <div
          className="sx-artist-personal-main"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '65vh',
            padding: '4rem 1.5rem',
          }}
        >
          <div
            style={{
              maxWidth: '540px',
              textAlign: 'center',
              padding: '3.5rem 2rem',
              background: '#5048B5',
              borderRadius: '24px',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.3)',
            }}
          >
            <AlertCircle size={48} color="#DBFF00" style={{ margin: '0 auto 1.25rem' }} />
            <h1 style={{ fontSize: '1.8rem', fontWeight: 800, marginBottom: '0.75rem', color: '#fff' }}>
              Artist Not Found
            </h1>
            <p style={{ color: 'rgba(255, 255, 255, 0.75)', lineHeight: 1.6, marginBottom: '2rem', fontSize: '0.95rem' }}>
              We could not locate an active public profile matching this address. The artist may have updated their Instagram handle, or their portfolio may currently be private.
            </p>
            <Link
              to="/artists"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: '#DBFF00',
                color: '#1a1542',
                fontWeight: 700,
                padding: '0.65rem 1.5rem',
                borderRadius: '9999px',
                textDecoration: 'none',
              }}
            >
              <ArrowLeft size={16} /> Explore Artist WEB
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // 6. Data Normalization & Preparation
  const displayName = (artist.stage_name || artist.name || 'Artist').trim();
  const initial = (displayName[0] || 'A').toUpperCase();
  const normalizedGenre = normalizeGenre(artist.genre);
  const normalizedSecondaryGenre = normalizeGenre(artist.secondary_genre);
  const genreText = [normalizedGenre, normalizedSecondaryGenre].filter(Boolean).join(' • ');
  const city = artist.city ? artist.city.trim() : '';

  // Star count resolved strictly from database (0-5 stars)
  const rawStars = Number(artist.star_count);
  const starCount = Number.isFinite(rawStars) && rawStars > 0 ? Math.min(5, Math.max(1, rawStars)) : 0;

  // Bio / Artist Web About (avoids displaying JSON payload if bio starts with '{')
  const rawBio = (
    artist.artist_web_about ||
    (artist.bio && !artist.bio.startsWith('{') ? artist.bio : '') ||
    ''
  ).trim();
  const bioDisplay = rawBio ? rawBio.replace(/\r?\n+/g, ' ') : '';

  // Handle for Top-Left Tab
  const atHandle = publicInstagram || normalizeInstagramUsername(artist.raw_instagram) || artist.slug || '';

  // SEO Metadata
  const seoTitle = `${displayName} | Artist Web | SenritsuX`;
  const seoDescription = bioDisplay || `${displayName} is an independent artist featured on SenritsuX Artist Web.`;

  return (
    <div className="sx-artist-personal-root">
      <SEO
        title={seoTitle}
        description={seoDescription}
        canonical={`/artist/${publicInstagram}`}
        ogType="profile"
        ogImage={artist.avatar_url || 'https://senritsux.in/og-image.png'}
        ogImageAlt={`${displayName} profile photo on SenritsuX`}
      />

      <Navbar />

      <main className="sx-artist-personal-main" aria-label={`${displayName} Artist Personal Page`}>
        {/* Top Bar: Back Link, Edit Action (Owner-Only), & Share Action */}
        <div className="sx-artist-top-bar">
          <Link to="/artists" className="sx-artist-back-link">
            <ArrowLeft size={16} /> SenritsuX Artist WEB
          </Link>

          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.65rem' }}>
            {isOwner && (
              <button
                type="button"
                onClick={handleOpenEdit}
                aria-label="Edit your platform links"
                className="sx-artist-edit-top-btn"
              >
                <Pencil size={14} /> Edit Links
              </button>
            )}

            <button
              type="button"
              onClick={handleCopyProfileLink}
              aria-label="Copy artist profile link to clipboard"
              className="sx-artist-share-btn"
            >
              {copied ? (
                <>
                  <Check size={14} color="#DBFF00" /> Link Copied!
                </>
              ) : (
                <>
                  <Share2 size={14} /> Share Profile
                </>
              )}
            </button>
          </div>
        </div>

        {/* Two-Column Master Layout */}
        <div className="sx-artist-personal-layout">
          {/* ================================================================
              LEFT: THE ARTIST SHEET
              ================================================================ */}
          <section
            className={`sx-artist-sheet-wrapper ${!atHandle ? 'no-tab' : ''}`}
            aria-label={`${displayName} Profile Sheet`}
          >
            {/* Top-Left Attached Handle Tab */}
            {atHandle && (
              <div className="sx-artist-sheet-handle-tab" aria-label={`Artist handle: @${atHandle}`}>
                <span className="sx-artist-tab-at">@</span>
                <span className="sx-artist-tab-handle">{atHandle}</span>
              </div>
            )}

            {/* Main Sheet Card Surface */}
            <div className="sx-artist-sheet-card">
              {/* Circular Avatar with Glowing Neon Lime Ring */}
              <div className="sx-artist-avatar-wrap">
                {artist.avatar_url && !avatarError ? (
                  <img
                    src={artist.avatar_url}
                    alt={`${displayName} artist profile photo`}
                    className="sx-artist-avatar-img"
                    onError={() => setAvatarError(true)}
                  />
                ) : (
                  <span className="sx-artist-avatar-fallback">{initial}</span>
                )}
              </div>

              {/* Stage Name & Verified Stars */}
              <div className="sx-artist-name-row">
                <h1 className="sx-artist-stage-name">{displayName}</h1>
                {starCount > 0 && (
                  <span
                    className="sx-artist-stars"
                    title={`${starCount} Star Verified Artist on SenritsuX`}
                    aria-label={`${starCount} star verified artist`}
                  >
                    {'★'.repeat(starCount)}
                  </span>
                )}
              </div>

              {/* Genre & City Badges */}
              {(genreText || city) && (
                <div className="sx-artist-badges-row">
                  {genreText && (
                    <span className="sx-artist-badge-genre">{genreText}</span>
                  )}
                  {city && (
                    <span className="sx-artist-badge-city">
                      <MapPin size={12} color="#DBFF00" /> {city}
                    </span>
                  )}
                </div>
              )}

              {/* Bio / Tagline */}
              {bioDisplay && (
                <p className="sx-artist-bio-text">{bioDisplay}</p>
              )}

              {/* Action Footer: Followers Count, Follow Button, and Propose Collab / Edit Links */}
              <div className="sx-artist-actions-row">
                <span className="sx-artist-follower-stat">
                  <Users2 size={16} color="#DBFF00" /> {followerCount} {followerCount === 1 ? 'Follower' : 'Followers'}
                </span>

                <button
                  type="button"
                  onClick={handleToggleFollow}
                  disabled={followPending || isOwner}
                  className="sx-artist-btn-follow"
                  aria-label={isFollowing ? 'Unfollow artist' : 'Follow artist'}
                >
                  {followPending ? (
                    'Updating...'
                  ) : isFollowing ? (
                    <>
                      <Check size={14} /> Following
                    </>
                  ) : (
                    <>
                      <UserPlus size={14} /> Follow
                    </>
                  )}
                </button>

                {/* If viewing own profile: show Edit button; If viewing another artist: show Propose Collab */}
                {isOwner ? (
                  <button
                    type="button"
                    onClick={handleOpenEdit}
                    className="sx-artist-btn-edit"
                    aria-label="Edit your platform links"
                  >
                    <Pencil size={14} /> Edit Links
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      if (!user) {
                        navigate('/login');
                        return;
                      }
                      setShowCollabModal(true);
                    }}
                    className="sx-artist-btn-collab"
                    aria-label={`Propose collaboration with ${displayName}`}
                  >
                    <Send size={14} /> Propose Collab
                  </button>
                )}
              </div>
            </div>
          </section>

          {/* ================================================================
              RIGHT: ALL PLATFORM LINKS COLUMN
              ================================================================ */}
          <aside className="sx-artist-platforms-column" aria-label="Official Platform and Streaming Links">
            {availablePlatformLinks.length > 0 ? (
              availablePlatformLinks.map((p) => (
                <a
                  key={p.id}
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sx-platform-pill"
                  aria-label={`Listen to ${displayName} on ${p.name} (opens in new tab)`}
                >
                  {p.icon}
                  <span className="sx-platform-label">
                    {p.labelComponent || p.label}
                  </span>
                  <div className="sx-platform-arrow" aria-hidden="true">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#1a1542"
                      strokeWidth="3.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </div>
                </a>
              ))
            ) : (
              <div className="sx-platform-empty-box">
                {isOwner ? (
                  <div>
                    <p style={{ margin: '0 0 0.85rem 0' }}>You have not added any platform links yet.</p>
                    <button
                      type="button"
                      onClick={handleOpenEdit}
                      className="sx-artist-btn-edit"
                      style={{ fontSize: '0.8rem', padding: '0.4rem 1.1rem' }}
                    >
                      <Plus size={14} /> Add Platform Links
                    </button>
                  </div>
                ) : (
                  'Official streaming and social links will appear here once added.'
                )}
              </div>
            )}
          </aside>
        </div>

        {/* ── ARTIST-ONLY EDIT MODAL ── */}
        {showEditModal && (
          <div
            className="sx-edit-modal-backdrop"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sx-edit-modal-title"
          >
            <div className="sx-edit-modal-card">
              <div className="sx-edit-modal-header">
                <div>
                  <h2 id="sx-edit-modal-title" className="sx-edit-modal-title">
                    Manage Platform Links
                  </h2>
                  <p className="sx-edit-modal-subtitle">
                    Add, edit, or remove your official streaming and social links displayed on your Artist Web profile.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  disabled={editSaving}
                  className="sx-edit-modal-close-btn"
                  aria-label="Close edit modal"
                >
                  <X size={18} />
                </button>
              </div>

              <form onSubmit={handleSaveLinks} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                <div className="sx-edit-modal-body">
                  {editingLinks.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem 1rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                      No links currently configured. Click below to add your first platform link!
                    </div>
                  ) : (
                    editingLinks.map((item, idx) => (
                      <div key={item.id} className="sx-edit-link-item">
                        <div className="sx-edit-link-top-row">
                          <select
                            value={item.platform}
                            onChange={(e) => handlePlatformChange(item.id, e.target.value)}
                            disabled={editSaving}
                            className="sx-edit-platform-select"
                            aria-label={`Select platform for link ${idx + 1}`}
                          >
                            {PREDEFINED_PLATFORMS.map((p) => (
                              <option key={p.key} value={p.key}>
                                {p.name}
                              </option>
                            ))}
                          </select>

                          <input
                            type="text"
                            value={item.name}
                            onChange={(e) => handleNameChange(item.id, e.target.value)}
                            disabled={editSaving}
                            placeholder="Platform Label"
                            className="sx-edit-custom-name-input"
                            aria-label={`Label for link ${idx + 1}`}
                          />

                          <button
                            type="button"
                            onClick={() => handleRemoveLink(item.id)}
                            disabled={editSaving}
                            className="sx-edit-link-remove-btn"
                            title="Remove this platform link"
                            aria-label={`Remove link ${idx + 1}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>

                        <div className="sx-edit-link-url-row">
                          <input
                            type="text"
                            value={item.url}
                            onChange={(e) => handleUrlChange(item.id, e.target.value)}
                            disabled={editSaving}
                            placeholder={PREDEFINED_PLATFORMS.find((p) => p.key === item.platform)?.placeholder || 'https://...'}
                            className={`sx-edit-url-input ${editErrors[`url_${item.id}`] ? 'has-error' : ''}`}
                            aria-label={`Destination URL for link ${idx + 1}`}
                          />
                          {editErrors[`url_${item.id}`] && (
                            <span className="sx-edit-input-error">
                              <AlertCircle size={12} /> {editErrors[`url_${item.id}`]}
                            </span>
                          )}
                          {editErrors[`name_${item.id}`] && (
                            <span className="sx-edit-input-error">
                              <AlertCircle size={12} /> {editErrors[`name_${item.id}`]}
                            </span>
                          )}
                        </div>
                      </div>
                    ))
                  )}

                  <button
                    type="button"
                    onClick={handleAddLink}
                    disabled={editSaving}
                    className="sx-edit-add-btn"
                  >
                    <Plus size={16} /> Add Another Platform Link
                  </button>

                  {editGlobalError && (
                    <div className="sx-edit-status-alert is-error">
                      <AlertCircle size={16} /> {editGlobalError}
                    </div>
                  )}

                  {editSuccessMsg && (
                    <div className="sx-edit-status-alert is-success">
                      <Check size={16} /> {editSuccessMsg}
                    </div>
                  )}
                </div>

                <div className="sx-edit-modal-footer">
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    disabled={editSaving}
                    className="sx-edit-btn-cancel"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={editSaving}
                    className="sx-edit-btn-save"
                  >
                    {editSaving ? (
                      'Saving Changes...'
                    ) : (
                      <>
                        <Check size={16} /> Save Changes
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ── COLLABORATION PROPOSAL MODAL ── */}
        {showCollabModal && (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="collab-modal-title"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(10, 8, 30, 0.8)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              zIndex: 2000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '1.25rem',
            }}
          >
            <div
              style={{
                width: '100%',
                maxWidth: '520px',
                padding: '2.5rem 2rem',
                position: 'relative',
                background: '#5048B5',
                borderRadius: '24px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 24px 60px rgba(0, 0, 0, 0.45)',
              }}
            >
              <h2
                id="collab-modal-title"
                style={{ fontSize: '1.4rem', fontWeight: 700, marginBottom: '0.4rem', color: '#fff' }}
              >
                Collab with {displayName}
              </h2>
              <p
                style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.88rem', marginBottom: '1.5rem' }}
              >
                Send a direct project proposal to {displayName}'s SenritsuX creator dashboard.
              </p>

              {collabSubmitted ? (
                <div
                  style={{
                    padding: '1.25rem',
                    borderRadius: '12px',
                    background: 'rgba(219, 255, 0, 0.12)',
                    border: '1px solid #DBFF00',
                    color: '#DBFF00',
                    textAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    fontWeight: 600,
                  }}
                >
                  <Check size={18} /> Proposal sent to {displayName}!
                </div>
              ) : (
                <form onSubmit={handleCollabSubmit}>
                  {collabError && (
                    <div
                      style={{
                        padding: '0.75rem 1rem',
                        borderRadius: '8px',
                        background: 'rgba(255, 75, 75, 0.15)',
                        border: '1px solid rgba(255, 75, 75, 0.4)',
                        color: '#ff6666',
                        fontSize: '0.85rem',
                        marginBottom: '1rem',
                      }}
                    >
                      {collabError}
                    </div>
                  )}

                  <div style={{ marginBottom: '1.25rem' }}>
                    <label
                      htmlFor="collab-project-type"
                      style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#ffffff', marginBottom: '0.4rem' }}
                    >
                      Project Type
                    </label>
                    <select
                      id="collab-project-type"
                      value={collabProjectType}
                      onChange={(e) => setCollabProjectType(e.target.value)}
                      disabled={collabSubmitting}
                      style={{
                        width: '100%',
                        padding: '0.65rem 0.9rem',
                        borderRadius: '10px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        background: 'rgba(20, 18, 55, 0.95)',
                        color: '#ffffff',
                        fontSize: '0.9rem',
                        boxSizing: 'border-box',
                      }}
                    >
                      <option value="Feature / Vocal">Feature / Vocalist</option>
                      <option value="Music Production / Beat">Music Production / Beat</option>
                      <option value="Mixing & Mastering">Mixing &amp; Mastering</option>
                      <option value="Songwriting / Lyricist">Songwriting / Lyricist</option>
                      <option value="Music Video / Editing">Music Video / Editing</option>
                      <option value="Other Joint Release">Other Joint Release</option>
                    </select>
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label
                      htmlFor="collab-message"
                      style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#ffffff', marginBottom: '0.4rem' }}
                    >
                      Proposal Message
                    </label>
                    <textarea
                      id="collab-message"
                      rows={4}
                      placeholder={`Tell ${displayName} about your project, timeline, and collaboration vision...`}
                      value={collabMessage}
                      onChange={(e) => setCollabMessage(e.target.value)}
                      disabled={collabSubmitting}
                      required
                      style={{
                        width: '100%',
                        padding: '0.65rem 0.9rem',
                        borderRadius: '10px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        background: 'rgba(20, 18, 55, 0.95)',
                        color: '#ffffff',
                        fontSize: '0.9rem',
                        resize: 'vertical',
                        minHeight: '90px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      onClick={() => setShowCollabModal(false)}
                      disabled={collabSubmitting}
                      style={{
                        padding: '0.6rem 1.25rem',
                        borderRadius: '9999px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        background: 'transparent',
                        color: '#ffffff',
                        fontSize: '0.88rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={collabSubmitting || !collabMessage.trim()}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.4rem',
                        padding: '0.6rem 1.4rem',
                        borderRadius: '9999px',
                        border: 'none',
                        background: '#DBFF00',
                        color: '#1a1542',
                        fontSize: '0.88rem',
                        fontWeight: 700,
                        cursor: collabSubmitting || !collabMessage.trim() ? 'default' : 'pointer',
                        opacity: collabSubmitting || !collabMessage.trim() ? 0.6 : 1,
                      }}
                    >
                      <Send size={15} /> {collabSubmitting ? 'Sending...' : 'Send Proposal'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
